import { useMemo } from "react";

import { useSearchPatients } from "@elation/api-client/src/stitch-web";

import { getHttpStatus } from "@src/shared/utils/http/errors";

import { PatientEntity } from "../../../../../domain";
import type { PatientSearchOption } from "../PatientSearch.types";

interface UsePatientSearchOptionsParams {
  disabled: boolean;
  limit: number;
  query: string;
}

export function usePatientSearchOptions({
  disabled,
  limit,
  query,
}: UsePatientSearchOptionsParams) {
  const patientSearch = useSearchPatients(
    {
      q: query.trim() || undefined,
      sortBy: "lastName",
      order: "asc",
      limit,
    },
    {
      query: {
        enabled: !disabled,
        retry: (failureCount, err) => {
          const status = getHttpStatus(err);
          if (status != null && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  );
  const options = useMemo<PatientSearchOption[]>(
    () =>
      (patientSearch.data?.data.items ?? []).map((patient) => {
        const name = PatientEntity.displayName(patient) ?? "Unknown patient";

        return {
          key:
            PatientEntity.searchResultKey(patient) ??
            `patient-search:${name}:${patient.externalPatientId ?? "unknown"}`,
          patient,
          name,
          initials: PatientEntity.initials(patient) ?? "?",
          metadata: PatientEntity.listMetadata(patient) ?? "",
          helperText:
            patient.patientId == null
              ? `Will sync from ${PatientEntity.ehrLabel(patient) ?? "EHR"}`
              : null,
        };
      }),
    [patientSearch.data],
  );

  return {
    options,
    isError: patientSearch.isError,
    error: patientSearch.error,
    isSearching: patientSearch.isLoading && options.length === 0,
  } as const;
}
